import { supabase, tabelaDoSchema } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Imagens do Nexus para um ticket.
//
// A API do MySuite não aceita anexo: o print que o Setor de Sistemas cola no
// Nexus ("segue imagem em anexo") fica só lá, e o operador do suporte lia a
// frase sem ver a imagem. O Orion e o Nexus usam o MESMO projeto do Supabase,
// e a chave anon já lê `nexus.anexos` e o bucket `nexus` (ver
// Nexus/nexus/database/20_anexos.sql) — então o resumo do ticket busca lá.
//
// O CAMINHO ATÉ A IMAGEM. Conferido em 27/10/2026: das 19 imagens do bucket,
// NENHUMA tinha `numero_ticket` ou `solicitacao_id` preenchidos — o Nexus as
// envia como "avulsos" e o único vínculo é o marcador `[[anexo:<id>]]` dentro
// do texto. Por isso o caminho é:
//
//   nº do ticket → nexus.solicitacoes (numero_ticket)
//                → textos da solicitação, das tarefas e das observações
//                → ids dos marcadores → nexus.anexos → URL assinada.
//
// As colunas `numero_ticket`/`solicitacao_id` de `nexus.anexos` entram também:
// hoje vêm vazias, mas são o vínculo direto se o Nexus passar a gravá-las.
//
// Tudo aqui é acessório: qualquer falha devolve lista vazia e o resumo segue.
// ─────────────────────────────────────────────────────────────────────────────

const BUCKET = 'nexus';
const PADRAO = /\[\[anexo:([0-9a-zA-Z-]{6,40})\]\]/g;

// A URL assinada vence; uma hora cobre com folga um resumo aberto.
const VALIDADE_URL_S = 60 * 60;

// Onde o Nexus aceita imagem no texto, com o rótulo que o operador vê.
const CAMPOS_SOLICITACAO = {
  descricao: 'Descrição', causa: 'Causa', solucao: 'Solução', plano_acao: 'Plano de ação',
};
const CAMPOS_TAREFA = {
  resumo_detalhado: 'Resumo', causa: 'Causa', realizado: 'Realizado',
  solucao_definitiva: 'Solução definitiva',
};

/** Ids dos marcadores `[[anexo:…]]` de um texto, na ordem em que aparecem. */
export function idsDosMarcadores(texto) {
  return [...String(texto || '').matchAll(PADRAO)].map(m => m[1]);
}

/**
 * Imagens do Nexus ligadas ao ticket, prontas para exibir:
 * `[{ id, nome, url, largura, altura, origem, criadoEm }]`, na ordem dos textos.
 */
export async function imagensDoNexus(numeroTicket) {
  const numero = String(numeroTicket || '').trim();
  if (!numero) return [];

  try {
    const { data: solicitacoes, error } = await tabelaDoSchema('nexus', 'solicitacoes')
      .select(`id, ${Object.keys(CAMPOS_SOLICITACAO).join(', ')}`)
      .eq('numero_ticket', numero);
    if (error) throw error;

    const idsSolicitacao = (solicitacoes || []).map(s => s.id);

    // id do anexo → de onde ele veio. O primeiro texto que cita ganha.
    const origens = new Map();
    const anotar = (texto, origem) => idsDosMarcadores(texto).forEach(id => {
      if (!origens.has(id)) origens.set(id, origem);
    });

    (solicitacoes || []).forEach(s => {
      Object.entries(CAMPOS_SOLICITACAO).forEach(([campo, rotulo]) => anotar(s[campo], rotulo));
    });

    if (idsSolicitacao.length) {
      const [tarefas, observacoes] = await Promise.all([
        tabelaDoSchema('nexus', 'tarefas')
          .select(`numero, ${Object.keys(CAMPOS_TAREFA).join(', ')}`)
          .in('solicitacao_id', idsSolicitacao)
          .order('ordem', { ascending: true }),
        tabelaDoSchema('nexus', 'observacoes')
          .select('texto')
          .in('solicitacao_id', idsSolicitacao)
          .order('criado_em', { ascending: true }),
      ]);
      (tarefas.data || []).forEach(t => {
        const prefixo = t.numero ? `Tarefa ${t.numero}` : 'Tarefa';
        Object.entries(CAMPOS_TAREFA).forEach(([campo, rotulo]) => anotar(t[campo], `${prefixo} · ${rotulo}`));
      });
      (observacoes.data || []).forEach(o => anotar(o.texto, 'Observação'));
    }

    // O vínculo direto e o dos marcadores, numa consulta só.
    const filtros = [`numero_ticket.eq.${numero}`];
    if (idsSolicitacao.length) filtros.push(`solicitacao_id.in.(${idsSolicitacao.join(',')})`);
    if (origens.size) filtros.push(`id.in.(${[...origens.keys()].join(',')})`);

    const { data: anexos, error: erroAnexos } = await tabelaDoSchema('nexus', 'anexos')
      .select('id, caminho, nome, largura, altura, criado_em')
      .eq('tipo', 'imagem')
      .or(filtros.join(','));
    if (erroAnexos) throw erroAnexos;
    if (!anexos?.length) return [];

    const { data: assinadas, error: erroUrl } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(anexos.map(a => a.caminho), VALIDADE_URL_S);
    if (erroUrl) throw erroUrl;
    const urlPorCaminho = new Map((assinadas || []).map(u => [u.path, u.signedUrl]));

    const ordem = [...origens.keys()];
    const posicao = (id) => { const i = ordem.indexOf(id); return i === -1 ? ordem.length : i; };

    return anexos
      .map(a => ({
        id: a.id,
        nome: a.nome,
        url: urlPorCaminho.get(a.caminho) || '',
        largura: a.largura,
        altura: a.altura,
        origem: origens.get(a.id) || 'Anexo',
        criadoEm: a.criado_em,
      }))
      .filter(a => a.url)
      .sort((x, y) => posicao(x.id) - posicao(y.id));
  } catch (err) {
    console.warn('[Orion] Imagens do Nexus:', err.message || err);
    return [];
  }
}
